import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  InlineNotification,
  NumberInput,
  SkeletonText
} from 'carbon-components-react';
import { Information16, ArrowRight16 } from '@carbon/icons-react';
import { FileUploadDropZone, UploadedFileRow } from 'components/PremiumWizard';
import {
  BelGeometrySettings,
  BelGeometryValidationErrorBanner,
  blockInvalidNumberInputKeyDown,
  formatGeometryDetectedLabel,
  getCarbonNumberInputRaw,
  hasPolygonGeometry,
  isAllowedBufferDistanceTyping,
  parseBufferDistanceRaw,
  parseMaxSplitAreaRaw
} from '../geometrySettings.utils';
import { parseBelUploadError } from '../constants/errorMessages';
interface UploadedFileDisplay {
  name: string;
  size: string;
}

const ALLOWED_EXTENSIONS = ['.kmz', '.kml', '.geojson', '.zip', '.gpkg'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

interface UploadStepProps {
  uploadedFile: UploadedFileDisplay | null;
  isUploading: boolean;
  isValidating: boolean;
  error: string | null;
  onFileSelected: (file: File) => void;
  onFileReplace: () => void;
  totalFeatures: number;
  geometryTypes: string[];
  totalAreaSqm: number;
  bufferDistance: number;
  maxSplitAreaKm: number;
  applyBuffer: boolean;
  applySplit: boolean;
  geometrySettings: BelGeometrySettings | null;
  geometryValidationErrorBanner: BelGeometryValidationErrorBanner | null;
  geometrySettingsCanProceed: boolean | null;
  isGeometryValidationLoading: boolean;
  onBufferDistanceChange: (value: number) => void;
  onMaxSplitAreaChange: (value: number) => void;
  onApplyBufferChange: (apply: boolean) => void;
  onApplySplitChange: (apply: boolean) => void;
  onGeometryInputsValidityChange: (valid: boolean) => void;
}

type GeometryBannerDisplay = 'pending' | 'error' | 'info' | 'none';

function getGeometryBannerDisplay(
  geometrySettingsCanProceed: boolean | null,
  geometryValidationErrorBanner: BelGeometryValidationErrorBanner | null
): GeometryBannerDisplay {
  if (geometrySettingsCanProceed === null) {
    return 'pending';
  }
  if (geometryValidationErrorBanner) {
    return 'error';
  }
  if (geometrySettingsCanProceed === true) {
    return 'info';
  }
  return 'none';
}

function getBufferNumberInputValue(raw: string): number | '' {
  return raw === '' ? '' : Number(raw);
}

function getSplitNumberInputValue(raw: string): number | '' {
  if (raw === '') {
    return '';
  }
  const normalized = raw.endsWith('.') ? raw.slice(0, -1) : raw;
  const value = Number(normalized);
  return Number.isNaN(value) ? '' : value;
}

/**
 * Resolves the typed/stepped string from Carbon NumberInput.
 * Steppers are rounded to one decimal; keyboard uses the raw target value.
 */
function getMaxSplitAreaRawFromCarbon(
  event: any,
  directionOrMeta?: string | { value?: number | string },
  stepperValue?: number
): string {
  const isStepperChange =
    typeof stepperValue === 'number' || event?.type === 'click';
  const carbonValue =
    typeof stepperValue === 'number'
      ? stepperValue
      : directionOrMeta && typeof directionOrMeta === 'object'
      ? Number(directionOrMeta.value)
      : NaN;

  if (isStepperChange && !Number.isNaN(carbonValue)) {
    return Number(carbonValue.toFixed(1)).toString();
  }

  return String(event?.target?.value ?? '');
}

const UploadStep: React.FC<UploadStepProps> = ({
  uploadedFile,
  isUploading,
  isValidating,
  error,
  onFileSelected,
  onFileReplace,
  totalFeatures,
  geometryTypes,
  totalAreaSqm,
  bufferDistance,
  maxSplitAreaKm,
  applyBuffer,
  applySplit,
  geometrySettings,
  geometryValidationErrorBanner,
  geometrySettingsCanProceed,
  isGeometryValidationLoading,
  onBufferDistanceChange,
  onMaxSplitAreaChange,
  onApplyBufferChange,
  onApplySplitChange,
  onGeometryInputsValidityChange
}) => {
  const uploadErrorNotification = parseBelUploadError(error);

  const showBusy = isUploading || isValidating;
  const fileValidated = uploadedFile && !showBusy && !error;
  const bufferMin = geometrySettings?.bufferMin ?? 10;
  const bufferMax = geometrySettings?.bufferMax ?? 200;
  const maxSplitMin = geometrySettings?.maxSplitMin ?? 1.0;
  const maxSplitMax = geometrySettings?.maxSplitMax ?? 19.0;
  const maxSplitHelperText = `${maxSplitMin.toFixed(1)} - ${maxSplitMax.toFixed(
    1
  )} km²`;

  const bufferInputEnabled =
    geometrySettings && (geometrySettings.bufferRequired || applyBuffer);
  const splitInputEnabled =
    geometrySettings &&
    geometrySettings.showSplit &&
    (geometrySettings.splitRequired || applySplit);

  const featureDetectedLabel = formatGeometryDetectedLabel(
    geometryTypes,
    totalFeatures
  );
  const showTotalArea = totalAreaSqm > 0 && hasPolygonGeometry(geometryTypes);

  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const [fileValidationError, setFileValidationError] = useState<string | null>(
    null
  );
  const [bufferFieldInvalid, setBufferFieldInvalid] = useState(false);
  const [splitFieldInvalid, setSplitFieldInvalid] = useState(false);
  const [bufferInputRaw, setBufferInputRaw] = useState(() =>
    String(bufferDistance)
  );
  const [splitInputRaw, setSplitInputRaw] = useState(() =>
    String(maxSplitAreaKm)
  );
  const geometryBannerDisplay = getGeometryBannerDisplay(
    geometrySettingsCanProceed,
    geometryValidationErrorBanner
  );

  const handleFileSelected = (file: File) => {
    setFileValidationError(null);
    setPendingFileName(file.name);
    onFileSelected(file);
  };

  const uploadedFileName = uploadedFile?.name;

  useEffect(() => {
    if (uploadedFile || error) {
      setPendingFileName(null);
    }
  }, [uploadedFile, error]);

  useEffect(() => {
    setBufferInputRaw(String(bufferDistance));
  }, [uploadedFileName, bufferDistance]);

  useEffect(() => {
    setSplitInputRaw(String(maxSplitAreaKm));
  }, [uploadedFileName, maxSplitAreaKm]);

  useEffect(() => {
    if (!geometrySettings || !bufferInputEnabled) {
      setBufferFieldInvalid(false);
      return;
    }
    setBufferFieldInvalid(
      !parseBufferDistanceRaw(bufferInputRaw, geometrySettings).valid
    );
  }, [bufferInputEnabled, geometrySettings, bufferInputRaw]);

  useEffect(() => {
    if (!geometrySettings || !splitInputEnabled) {
      setSplitFieldInvalid(false);
      return;
    }
    setSplitFieldInvalid(
      !parseMaxSplitAreaRaw(splitInputRaw, geometrySettings).valid
    );
  }, [splitInputEnabled, geometrySettings, splitInputRaw]);

  useEffect(() => {
    const bufferOk = !bufferInputEnabled || !bufferFieldInvalid;
    const splitOk = !splitInputEnabled || !splitFieldInvalid;
    onGeometryInputsValidityChange(bufferOk && splitOk);
  }, [
    bufferInputEnabled,
    splitInputEnabled,
    bufferFieldInvalid,
    splitFieldInvalid,
    onGeometryInputsValidityChange
  ]);

  const handleBufferDistanceChange = (
    event: any,
    directionOrMeta?: string | { value?: number | string },
    stepperValue?: number
  ) => {
    if (!geometrySettings) {
      return;
    }
    const raw =
      getCarbonNumberInputRaw(event, directionOrMeta, stepperValue) ?? '';
    if (!isAllowedBufferDistanceTyping(raw)) {
      return;
    }
    setBufferInputRaw(raw);
    const parsed = parseBufferDistanceRaw(raw, geometrySettings);
    if (parsed.valid && parsed.value !== null) {
      onBufferDistanceChange(parsed.value);
    }
  };

  const handleMaxSplitAreaChange = (
    event: any,
    directionOrMeta?: string | { value?: number | string },
    stepperValue?: number
  ) => {
    if (!geometrySettings) {
      return;
    }
    const raw = getMaxSplitAreaRawFromCarbon(
      event,
      directionOrMeta,
      stepperValue
    );
    setSplitInputRaw(raw);
    const parsed = parseMaxSplitAreaRaw(raw, geometrySettings);
    if (parsed.valid && parsed.value !== null) {
      onMaxSplitAreaChange(parsed.value);
    }
  };

  const displayFile =
    uploadedFile ??
    (pendingFileName ? { name: pendingFileName, size: '' } : null);

  return (
    <div>
      <div className="bel-step__header">Upload file</div>

      <div className="bel-upload-step__upload-panel">
        {!displayFile ? (
          <>
            {fileValidationError && (
              <InlineNotification
                kind="error"
                lowContrast
                title="Invalid file"
                subtitle={fileValidationError}
                onCloseButtonClick={() => setFileValidationError(null)}
                className="bel-upload-step__notification bel-upload-step__notification--mb"
              />
            )}
            <FileUploadDropZone
              acceptedFormats="GeoJSON, GeoPackage, KMZ, Shapefile (ZIP), TAB (ZIP)"
              maxSize="10MB"
              allowedExtensions={ALLOWED_EXTENSIONS}
              maxSizeBytes={MAX_FILE_SIZE_BYTES}
              onFileSelected={handleFileSelected}
              onValidationError={setFileValidationError}
            />
          </>
        ) : (
          <>
            {fileValidated && !error && (
              <InlineNotification
                kind="success"
                title="File validated successfully"
                lowContrast
                hideCloseButton
                className="bel-upload-step__notification bel-upload-step__notification--mb"
              />
            )}
            {!showBusy && uploadErrorNotification && (
              <InlineNotification
                kind={uploadErrorNotification.kind}
                title={uploadErrorNotification.title}
                subtitle={uploadErrorNotification.subtitle}
                lowContrast
                onCloseButtonClick={onFileReplace}
                className="bel-upload-step__notification bel-upload-step__notification--mb"
              />
            )}
            <UploadedFileRow
              filename={displayFile.name}
              metadata={
                showBusy
                  ? isUploading
                    ? 'Uploading…'
                    : 'Validating file…'
                  : displayFile.size
              }
              loading={showBusy}
              onReplace={onFileReplace}
              disabled={showBusy}
            />
            {fileValidated && featureDetectedLabel && (
              <p className="bel-upload-step__feature-info">
                <Information16 />
                <span>
                  {featureDetectedLabel}
                  {showTotalArea &&
                    ` · Total area ${(totalAreaSqm / 1_000_000).toFixed(
                      2
                    )} km²`}
                </span>
              </p>
            )}
          </>
        )}
      </div>

      {fileValidated && geometrySettings && totalFeatures > 0 && (
        <div className="bel-upload-step__geometry-settings">
          {geometrySettings.headerText && (
            <div className="bel-step__header">
              {geometrySettings.headerText}
            </div>
          )}

          <div className="bel-upload-step__geometry-layout">
            <div className="bel-upload-step__section">
              {geometryBannerDisplay !== 'none' && (
                <div className="bel-upload-step__notification">
                  {geometryBannerDisplay === 'pending' && (
                    <div
                      className="bel-upload-step__geometry-banner-skeleton"
                      aria-live="polite"
                      aria-busy={isGeometryValidationLoading}
                      style={{
                        padding: '0.875rem 1rem',
                        backgroundColor: '#f4f4f4',
                        borderLeft: '3px solid #8d8d8d'
                      }}
                    >
                      <SkeletonText heading width="35%" />
                      <SkeletonText width="90%" />
                    </div>
                  )}
                  {geometryBannerDisplay === 'error' &&
                    geometryValidationErrorBanner && (
                      <InlineNotification
                        kind="error"
                        title={geometryValidationErrorBanner.title}
                        subtitle={geometryValidationErrorBanner.message}
                        lowContrast
                        hideCloseButton
                      />
                    )}
                  {geometryBannerDisplay === 'info' && (
                    <InlineNotification
                      kind="info"
                      title=""
                      subtitle={geometrySettings.infoBannerText}
                      lowContrast
                      hideCloseButton
                    />
                  )}
                </div>
              )}

              {geometrySettings.bufferOptional && (
                <div className="bel-upload-step__geometry-checkbox">
                  <Checkbox
                    id="bel-apply-buffer"
                    labelText="Apply buffer distance (optional)"
                    checked={applyBuffer}
                    onChange={onApplyBufferChange}
                  />
                </div>
              )}

              <div
                className={`bel-upload-step__buffer-section${
                  !bufferInputEnabled
                    ? ' bel-upload-step__buffer-section--disabled'
                    : ''
                }`}
              >
                <NumberInput
                  id="buffer-distance-input"
                  label="Buffer distance (in metres)"
                  value={getBufferNumberInputValue(bufferInputRaw)}
                  min={bufferMin}
                  max={bufferMax}
                  step={1}
                  disabled={!bufferInputEnabled}
                  invalid={Boolean(bufferInputEnabled) && bufferFieldInvalid}
                  invalidText={`Enter a value between ${bufferMin} m and ${bufferMax} m`}
                  helperText={
                    <>
                      {`Min. ${bufferMin} m · Max. ${bufferMax} m`}
                      <br />
                      {geometrySettings.bufferHelperText}
                    </>
                  }
                  onKeyDown={(event) =>
                    blockInvalidNumberInputKeyDown(event, {
                      allowDecimal: false
                    })
                  }
                  onChange={handleBufferDistanceChange}
                />
                <span className="bel-upload-step__buffer-unit">m</span>
              </div>

              {geometrySettings.showSplit && (
                <>
                  <div className="bel-upload-step__geometry-divider" />

                  {geometrySettings.splitOptional && (
                    <div className="bel-upload-step__geometry-checkbox">
                      <Checkbox
                        id="bel-apply-split"
                        labelText="Split shape (optional)"
                        checked={applySplit}
                        onChange={onApplySplitChange}
                      />
                    </div>
                  )}

                  <div
                    className={`bel-upload-step__split-section${
                      !splitInputEnabled
                        ? ' bel-upload-step__split-section--disabled'
                        : ''
                    }`}
                  >
                    <NumberInput
                      id="max-split-area-input"
                      label="Maximum area per enquiry (in kilometres)"
                      value={getSplitNumberInputValue(splitInputRaw)}
                      min={maxSplitMin}
                      max={maxSplitMax}
                      step={0.1}
                      disabled={!splitInputEnabled}
                      invalid={Boolean(splitInputEnabled) && splitFieldInvalid}
                      invalidText={`Enter a value between ${maxSplitMin.toFixed(
                        1
                      )} km² and ${maxSplitMax.toFixed(1)} km²`}
                      helperText={maxSplitHelperText}
                      onKeyDown={(event) =>
                        blockInvalidNumberInputKeyDown(event, {
                          allowDecimal: true
                        })
                      }
                      onChange={handleMaxSplitAreaChange}
                    />
                    <span className="bel-upload-step__area-unit">km²</span>
                  </div>
                </>
              )}
            </div>

            <div className="bel-upload-step__geometry-guidelines">
              <div className="bel-upload-step__guideline">
                <div className="bel-upload-step__guideline-title">
                  Buffer distance
                </div>
                <div className="bel-upload-step__guideline-illustration">
                  <div className="bel-upload-step__guideline-step">
                    <img
                      className="bel-upload-step__guideline-image-buffer-distance"
                      src="../premium-features/img-buffer-distance-line-geometry.png"
                      alt="Line geometry"
                    />
                    <p className="bel-upload-step__guideline-caption-line">
                      Line geometry
                    </p>
                  </div>
                  <ArrowRight16
                    aria-hidden="true"
                    className="bel-upload-step__guideline-arrow"
                  />
                  <div className="bel-upload-step__guideline-step">
                    <img
                      className="bel-upload-step__guideline-image-buffer-distance"
                      src="../premium-features/img-buffer-distance-buffer.png"
                      alt="Line geometry with buffer applied"
                    />
                    <p className="bel-upload-step__guideline-caption-buffer">
                      + buffer
                    </p>
                  </div>
                </div>
                <p className="bel-upload-step__guideline-description">
                  A buffer expands lines, polygons and points into a lodgeable
                  area
                </p>
              </div>

              {geometrySettings.showSplit && (
                <>
                  <div className="bel-upload-step__geometry-divider bel-upload-step__geometry-divider--guidelines" />
                  <div className="bel-upload-step__guideline bel-upload-step__guideline--split">
                    <div className="bel-upload-step__guideline-title">
                      Maximum area - splitting
                    </div>
                    <div className="bel-upload-step__guideline-illustration">
                      <div className="bel-upload-step__guideline-step">
                        <img
                          className="bel-upload-step__guideline-image"
                          src="../premium-features/img-maximum-area-1-large-shape.png"
                          alt="One large shape"
                        />
                        <p className="bel-upload-step__guideline-caption-maximum-area">
                          1 large shape
                        </p>
                      </div>
                      <ArrowRight16
                        aria-hidden="true"
                        className="bel-upload-step__guideline-arrow"
                      />
                      <div className="bel-upload-step__guideline-step">
                        <img
                          className="bel-upload-step__guideline-image"
                          src="../premium-features/img-maximum-area-split-into-smaller-enquiries.png"
                          alt="Shape split into smaller enquiries"
                        />
                        <p className="bel-upload-step__guideline-caption-maximum-area">
                          Split into smaller enquiries
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadStep;
